import { ResponsiveBar } from "@nivo/bar"
import React from "react"

export function HeatmapTooltip({ cell, maxPercent }) {
  if (!cell.data?.denominator || cell.data.denominator <= 0) return null

  function addAlpha(color, alpha = 0.5) {
    if (color.startsWith('#')) {
      // Convert hex to rgba
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    } else if (color.startsWith('rgb(')) {
      // Convert "rgb(...)" to "rgba(..., alpha)"
      return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
    }
    return color // Return as is if format is unrecognized
  }

  // 1) Extract fields from cell
  const {
    serieId,
    color: cellColor,
    data: {
      x: labelOrYear,
      cipPercent = 0,
      demoPercent = 0,
      numerator = 0,
      denominator = 0,
      demoNumerator = 0,
      demoDenominator = 0,
    },
  } = cell

  // 2) Simple formatter for numbers
  const fmt = (val) =>
    val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

  // 3) Build stacked bar data for two rows: "District" and "CIP".
  //    Each row includes its "actual" portion and an "extra" portion.
  const barData = [
    {
      id: "District",
      map: labelOrYear,
      denominator: fmt(demoDenominator),
      numerator: fmt(demoNumerator),
      studentGroup: serieId,
      studentDemo: demoPercent,
      // When CIP is higher than District, show missing extra for District.
      cipExtra: cipPercent > demoPercent ? cipPercent - demoPercent : null,
    },
    {
      id: "CIP",
      map: labelOrYear,
      denominator: fmt(denominator),
      numerator: fmt(numerator),
      studentGroup: serieId,
      cipDemo: cipPercent > 0 ? cipPercent : 0,
      // When District is higher than CIP, show missing extra for CIP.
      schoolExtra: demoPercent > cipPercent ? demoPercent - cipPercent : null,
    },
  ]

  // 4) Define full-opacity base colors
  const CIP_COLOR = "#58649ea2"  // for CIP row
  const DIST_COLOR = "#b9b8cea2" // for District row

  // 5) Define half-opacity colors for the extra segments
  const CIP_EXTRA_COLOR = "rgba(88,100,158,0.5)"
  const DIST_EXTRA_COLOR = "rgba(185,184,206,0.5)"

  // 6) Custom layer to draw a dashed outline around the extra segments.
  const dashedOutlineLayer = ({ bars }) => (
   
    <g>
      {bars
        .filter((bar) =>
          ((bar.id === "CIP" &&
          
            bar.width > 0) ||
          (bar.data.id === "schoolExtra" &&
            bar.data.indexValue === "CIP" &&
            bar.width > 0))
            
        )
        .map((bar) => {
          // For an extra slice, choose the boundary based on width
          const xPos = bar.width >= 0 ? bar.x + bar.width : bar.x
          return (
            <line
              key={bar.key + 1}
              x1={xPos}
              x2={xPos}
              y1={bar.y + bar.height / 2 - 75}
              y2={bar.y + bar.height / 2 + 75}
              stroke={'oklch(0.808 0.114 19.571)'}
              strokeDasharray="5 4"
              strokeWidth={2}
              strokeLinecap="round"
            />
          )
        })}
        
    </g>
  )



  // ...
// 2) Calculate difference
const diffPercent = demoPercent - cipPercent // If positive => CIP is missing
const diffCount = Math.round(Math.abs(diffPercent) * denominator)

// 3) Build a React element with the bolded count:
let differencePhrase = null
if (diffCount > 0) {
  differencePhrase = (
    <span className="ml-2 text-xs font-normal text-gray-600">
      <strong>{diffCount}</strong>{" "} enrollments {" "}
      {diffPercent > 0 ? "below" : "above"} district representation
    </span>
  )
}



  const differenceIndicatorLayer = ({ bars }) => {
    // 1) Find CIP and District bars
    const cipBar = bars.find((b) => b.key === "cipDemo.CIP");
    const distBar = bars.find((b) => b.key === "studentDemo.District");
    if (!cipBar || !distBar) {
      return null;
    }
  
    // 2) The “right” edges
    const cipRight = cipBar.x + cipBar.width;
    const distRight = distBar.x + distBar.width;
  
    // 3) Suppose you have logic to compute these
    const diffPercent = demoPercent - cipPercent;
    const missingCount = Math.round(Math.abs(diffPercent) * denominator);
    if (missingCount <= 0) return null;
  
    // 4) Color depends on whether CIP is missing or extra
    const isMissing = diffPercent > 0; // bigger District => CIP missing
    const bracketColor = isMissing ? "#E54D2E" : "#60B3D7";
  
    // 5) Bracket geometry
    const x1 = Math.min(cipRight, distRight);
    const x2 = Math.max(cipRight, distRight);
    const xMid = (x1 + x2) / 2;
    const bracketY = cipBar.y - 8; // bracket above CIP bar
  
    // -- The label text
    const labelText = `x${missingCount}`;
  
    // 6) Sizing for icon, etc.
    const iconSize = 14;
    const spacing = 2;
  
    // 7) Estimate text width
    //    We'll guess 6px per character for a ~0.7rem font. Tweak as needed.
    const approxCharWidth = 6;
    const textWidth = labelText.length * approxCharWidth;
  
    // 8) Add the icon’s space (iconSize + spacing) plus some padding on each side
    const horizontalPadding = 8; // extra left/right space
    const dynamicLabelWidth = iconSize + spacing + textWidth + horizontalPadding;
  
    // 9) Label height is relatively small text
    const labelHeight = 15;
  
    return (
      <g>
        {/* Bracket line */}
        <line
          x1={x1}
          x2={x2}
          y1={bracketY}
          y2={bracketY}
          stroke={bracketColor}
          strokeWidth={1.5}
        />
  
        {/* Ticks at each end */}
        <line
          x1={x1}
          x2={x1}
          y1={bracketY - 3}
          y2={bracketY + 3}
          stroke={bracketColor}
          strokeWidth={2}
        />
        <line
          x1={x2}
          x2={x2}
          y1={bracketY - 3}
          y2={bracketY + 3}
          stroke={bracketColor}
          strokeWidth={2}
        />
  
        {/* 
          Group for the label (icon + “x###”), with a white rect behind.
          We'll center it horizontally at xMid, 
          and place it exactly at bracketY (or bracketY - some offset).
        */}
        <g transform={`translate(${xMid}, ${bracketY})`}>
          {/* White background rect, centered horizontally */}
          <rect
            x={-dynamicLabelWidth / 2}
            y={-labelHeight / 2}
            width={dynamicLabelWidth}
            height={labelHeight}
            fill="#fff"
            rx={2}
            ry={2}
          />
  
          {/* Person icon - placed on the left inside the rect */}
          <svg
            x={-dynamicLabelWidth / 2 + 4}
            y={-iconSize / 2}
            width={iconSize}
            height={iconSize}
            fill={bracketColor}
            viewBox="0 0 16 16"
          >
            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14 
                     c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0 
                     c-.22.578.254 1.139.872 1.139h9.47Z"/>
          </svg>
  
          {/* Text: x### to the right of the icon */}
          <text
            x={-dynamicLabelWidth / 2 + 4 + iconSize + spacing}
            y={5} // a bit down from vertical center
            textAnchor="start"
            style={{
              fill: bracketColor,
              fontWeight: "bold",
              fontSize: "0.7rem",
              fontFamily: "Helvetica, Arial, sans-serif",
            }}
          >
            {labelText}
          </text>
        </g>
      </g>
    );
  };


 // Custom layer to draw the "missing" label on the right edge of CIP bars.
 const missingIndicatorLayer = ({ bars }) => {
  return (
    <g>
      {bars
        .filter((bar) => bar.key === "cipDemo.CIP")
        .map((bar) => {
          // Calculate how many students are missing.
          const missingCount = Math.round((demoPercent - cipPercent) * denominator)
          if (missingCount <= 0) return null

          // Anchor the label at the right end of the bar.
          const xRight = bar.x + bar.width
          const yMiddle = bar.y + bar.height / 2

          // Label geometry
          const labelWidth = 50
          const labelHeight = 16
          const iconSize = 10
          const spacing = 4

          return (
            <g key={bar.key} transform={`translate(${xRight}, ${yMiddle})`}>
              <rect
                x={0}
                y={-labelHeight / 2}
                width={labelWidth}
                height={labelHeight}
                fill="#ffffff"
                rx={2}
                ry={2}
              />
              <g transform="translate(2, -5) scale(0.5)">
                <path
                  fill="rgba(255,0,0,0.7)"
                  d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                />
              </g>
              <text
                x={iconSize + spacing}
                y={3}
                textAnchor="start"
                style={{
                  fill: "rgba(255,0,0,0.7)",
                  fontWeight: "bold",
                  fontSize: "0.65rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                }}
              >
                x {missingCount}
              </text>
            </g>
          )
        })}
    </g>
  )
}


  return (
    <div className="bg-white p-2 rounded-md shadow-lg text-left relative w-[320px] border-2 border-zinc-200">
    {/* --- TOP ROW: Group, Year --- */}
    <div className="flex items-center text-sm font-medium text-gray-900">
      <span>{serieId}, {labelOrYear}</span>
      {/* Renders the difference phrase if it exists (with bolded count) */}
    
    </div>
  {differencePhrase}

      {/* Horizontal Bar Chart */}
      <div className="w-[300px] h-[100px] border-b-2 border-slate-100 mb-1">
        
        <ResponsiveBar
        maxValue={maxPercent}
          motionConfig="stiff"
          layout="horizontal"
          data={barData}
          keys={["cipDemo", "studentDemo", "cipExtra", "schoolExtra"]}
          groupMode="stacked"
          margin={{ top: 15, right: 0, bottom: 0, left: 60 }}
          padding={0.15}
          innerPadding={2}
          valueFormat=">1.0~%"
          yScale={{ type: "linear", stacked: true }}
          axisBottom={false}
          axisLeft={{
            tickSize: 8,
            tickPadding: 5,
            tickValues: 4,
          }}
          enableGridX={false}
          enableGridY
          borderRadius={2}
          borderWidth={0}
          // For the main solid color segments
          colors={({ id }) => {
            if (id === "cipDemo") return CIP_COLOR
            if (id === "studentDemo") return DIST_COLOR
            return "#000000"
          }}
          // Define the candycane pattern for extra segments
          defs={[
            {
              id: 'candycane',
              type: 'patternLines',
              background: 'white',
              color: addAlpha(cellColor, 0.2),
              rotation: -45,
              lineWidth: 1,
              spacing: 3,
            },
          ]}
          fill={[
            {
              match: { id: 'cipExtra' },
              id: 'candycane',
            },
            {
              match: { id: 'schoolExtra' },
              id: 'candycane',
            },
          ]}
          markers={[
            {
              axis: "x",
              value: 0,
              lineStyle: {
                stroke: "#ffffff",
                strokeWidth: 1,
                strokeDasharray: "2 1",
              },
            },
          ]}
          // Include custom layers for the dashed outline and missing indicator.
          layers={[
            "bars",
            differenceIndicatorLayer,
            "axes",
          ]}
          labelSkipWidth={0}
          labelSkipHeight={0}
          labelTextColor={(bar) => {
            const { id } = bar.data
            if (id === "cipDemo" || id === "studentDemo") return "#1d1d1d"
            return cellColor
          }}
        />
      </div>

      <div className="flex flex-col items-center border-b pb-4">
  <div className="flex flex-row items-center justify-center space-x-8">

    {/* CIP Column */}
    <div className="flex flex-col items-center">
      {/* Label + color indicator */}
      <div className="flex items-center">
        <div
          className="h-2 w-2 rounded-full mr-1"
          style={{ backgroundColor: CIP_COLOR }}
        />
        <span className="text-xs text-gray-600 font-semibold">CIP</span>
      </div>

      {/* Percentage + fraction in one row */}
      <div className="flex flex-row items-center mt-2 text-black">
        <span className="text-2xl font-bold">
          {`${(cipPercent * 100).toFixed(0)}%`}
        </span>
        <div className="flex flex-col items-center ml-2">
          <div>{fmt(numerator)}</div>
          <hr className="border-t border-gray-300 w-8 my-1" />
          <div>{fmt(denominator)}</div>
        </div>
      </div>
    </div>

    {/* VS */}
    <span className="text-xl font-bold text-gray-700">vs</span>

    {/* District Column */}
    <div className="flex flex-col items-center">
      {/* Label + color indicator */}
      <div className="flex items-center">
        <div
          className="h-2 w-2 rounded-full mr-1"
          style={{ backgroundColor: DIST_COLOR }}
        />
        <span className="text-xs text-gray-600 font-semibold">District</span>
      </div>

      {/* Percentage + fraction in one row */}
      <div className="flex flex-row items-center mt-2 text-black">
        <span className="text-2xl font-bold">
          {`${(demoPercent * 100).toFixed(0)}%`}
        </span>
        <div className="flex flex-col items-center ml-2">
          <div>{fmt(demoNumerator)}</div>
          <hr className="border-t border-gray-300 w-8 my-1" />
          <div>{fmt(demoDenominator)}</div>
        </div>
      </div>
    </div>
  </div>

 
</div>
</div>
  )
}